import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import type { FC } from "react";
import { CONTROL_HEIGHT_PX, FONT_SIZE, ICON_SIZE, INK, NEUTRAL } from "../../../theme/tokens";
import { FILTER_SEARCH_CANCEL_BUTTON_SX, filterSearchHtmlInputProps } from "./filterSearchFieldProps";
import { SystemValidationTextField } from "./SystemValidationTextField";

type Props = {
	value: string;
	/** The whole new term — typed, or `""` from the clear button. */
	onChange: (next: string) => void;
	/** Already translated, and says what the search reaches ("Search staff who could take it…"). */
	placeholder: string;
	/**
	 * The input's `name`: stable and not identity-shaped, so password managers leave the field alone (see
	 * `filterSearchHtmlInputProps`).
	 */
	name: string;
	dataTestId: string;
};

/**
 * The search field a bottom sheet pins over the list it narrows — `SheetStep`'s `toolbar`, above a list of people to
 * pick from.
 *
 * Touch-sized rather than toolbar-sized: `CONTROL_HEIGHT.STANDARD` with `EMPHASIS` type, a quiet `NEUTRAL[50]` fill so it
 * reads as part of the heading rather than a form field, a leading search glyph at the sheet rows' `ICON_SIZE.MENU_ROW`,
 * and the field's own clear button once there is something to clear. It is also a `type="search"` filter box that
 * password managers skip, like every search input in the app.
 */
export const SheetSearchField: FC<Props> = ({ value, onChange, placeholder, name, dataTestId }) => (
	<SystemValidationTextField
		value={value}
		onChange={(event) => onChange(event.target.value)}
		onClear={value ? () => onChange("") : undefined}
		placeholder={placeholder}
		reserveHeightForHelperText={false}
		inputType="search"
		injectedProps={{ slotProps: { htmlInput: filterSearchHtmlInputProps(name) } }}
		inputSx={{
			...FILTER_SEARCH_CANCEL_BUTTON_SX,
			"& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT_PX.STANDARD, fontSize: FONT_SIZE.EMPHASIS, bgcolor: NEUTRAL[50] },
		}}
		inputProps={{
			startAdornment: (
				<InputAdornment position="start">
					<SearchIcon sx={{ fontSize: ICON_SIZE.MENU_ROW, color: INK.MUTED }} />
				</InputAdornment>
			),
		}}
		fullWidth
		dataTestId={dataTestId}
	/>
);
